import {Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {take} from 'rxjs/operators';
import {Menu, MenuService, Weekday, WeekdayMenu, WeeklyMenu} from '../../services/menu/menu.service';

interface HeroSlide {
    image: string;
    alt: string;
}

interface Highlight {
    image: string;
    eyebrow: string;
    title: string;
    text: string;
    link?: string;
    external?: boolean;
}

interface GalleryImage {
    src: string;
    alt: string;
}

interface OpeningState {
    open: boolean;
    label: string;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
    })
export class HomeComponent implements OnInit, OnDestroy {

    readonly heroSlides: HeroSlide[] = [
        {image: 'assets/img/gallery/landwirte-rinder.jpg', alt: 'Landwirte aus der Region bei ihren Rindern'},
        {image: 'assets/img/gallery/theke-02.jpg', alt: 'Feinkosttheke der Metzgerei Rettensteiner-Scharfetter'},
        {image: 'assets/img/gallery/stube-01.jpg', alt: 'Gaststube der Marktstubn'},
        {image: 'assets/img/gallery/terrasse-02.jpg', alt: 'Sonnenterrasse der Marktstubn mit Blick auf die Berge'}
    ];

    readonly highlights: Highlight[] = [
        {
            image: 'assets/img/gallery/theke-01.jpg',
            eyebrow: 'Metzgerei',
            title: 'Hausschlachtung & eigene Reifung',
            text: 'Rindfleisch aus der hauseigenen Schlachterei, Schweinefleisch aus den besten österreichischen Betrieben und Wurstspezialitäten nach eigenen Rezepturen.'
        },
        {
            image: 'assets/img/gallery/stube-04.jpg',
            eyebrow: 'Marktstub\'n',
            title: 'Mittagstisch mitten in St. Johann',
            text: 'Zwei wechselnde Menüs, Wochenkarte und Frühstück – gemütlich in der Stube oder auf der Sonnenterrasse.',
            link: '/speiseplan'
        },
        {
            image: 'assets/img/gallery/landwirte-rinder.jpg',
            eyebrow: 'Herkunft',
            title: 'Bauern, die wir beim Namen kennen',
            text: 'Unsere Tiere kommen von Höfen aus der Umgebung. Kurze Wege, echte Partnerschaften und volle Transparenz bis auf den Teller.',
            link: '/qualität'
        },
        {
            image: 'assets/img/gallery/feinkost.jpg',
            eyebrow: 'Feinkost',
            title: 'Für die Jause und den guten Abend',
            text: 'Käse, Antipasti, Nudeln, Öle und Bauernschnäpse aus der Region ergänzen unser Sortiment.'
        }
    ];

    readonly galleryImages: GalleryImage[] = [
        {src: 'assets/img/gallery/theke-02.jpg', alt: 'Blick in die Verkaufstheke'},
        {src: 'assets/img/gallery/stube-02.jpg', alt: 'Gaststube mit Wildmotiven'},
        {src: 'assets/img/gallery/terrasse-02.jpg', alt: 'Gäste auf der Sonnenterrasse'},
        {src: 'assets/img/gallery/feinkost.jpg', alt: 'Feinkostregal mit Spezialitäten'},
        {src: 'assets/img/gallery/stube-05.jpg', alt: 'Stube mit Baum und Zirbenholz'},
        {src: 'assets/img/gallery/terrasse-04.jpg', alt: 'Gedeckter Tisch auf der Terrasse'},
        {src: 'assets/img/gallery/stube-03.jpg', alt: 'Stube mit Blick auf die Berge'},
        {src: 'assets/img/gallery/imbiss.jpg', alt: 'Imbiss- und Kaffeetheke'},
        {src: 'assets/img/gallery/stube-09.jpg', alt: 'Zirbenstube mit Geweih'},
        {src: 'assets/img/gallery/terrasse-01.jpg', alt: 'Terrasse mit Sonnenschirmen'},
        {src: 'assets/img/gallery/stube-07.jpg', alt: 'Gaststube mit Steinbrunnen'},
        {src: 'assets/img/gallery/landwirte-rinder.jpg', alt: 'Landwirte im Stall bei ihren Rindern'},
        {src: 'assets/img/gallery/stube-06.jpg', alt: 'Gemütliche Sitzecke in der Marktstubn'}
    ];

    activeSlide = 0;
    heroScrollProgress = 0;

    todayWeekday?: Weekday;
    /** False on weekends, where we preview the next weekday instead. */
    isToday = false;
    todayMenus: Menu[] = [];
    weekdayMenus: WeekdayMenu[] = [];
    weeklyMenus: WeeklyMenu[] = [];
    menusLoaded = false;

    openingState: OpeningState = {open: false, label: 'Öffnungszeiten ansehen'};

    lightboxIndex: number | null = null;

    private slideTimer?: ReturnType<typeof setInterval>;

    constructor(private menuService: MenuService,
                @Inject(PLATFORM_ID) private platformId: object) {
    }

    ngOnInit(): void {
        this.todayWeekday = this.menuService.getCurrentWeekday();
        this.isToday = !!this.todayWeekday;
        this.openingState = this.resolveOpeningState(new Date());

        this.menuService.getWeekdayMenus()
            .pipe(take(1))
            .subscribe((weekdayMenus: WeekdayMenu[]): void => {
                this.weekdayMenus = weekdayMenus;
                this.todayMenus = this.resolveTodayMenus(weekdayMenus);
                this.menusLoaded = true;
            });

        this.menuService.getWeeklyMenus()
            .pipe(take(1))
            .subscribe((weeklyMenus: WeeklyMenu[]): void => {
                this.weeklyMenus = weeklyMenus.filter(weeklyMenu => !!weeklyMenu.menu.hauptspeise);
            });

        if (isPlatformBrowser(this.platformId)) {
            this.slideTimer = setInterval((): void => {
                this.activeSlide = (this.activeSlide + 1) % this.heroSlides.length;
            }, 6000);
        }
    }

    ngOnDestroy(): void {
        if (this.slideTimer) {
            clearInterval(this.slideTimer);
        }
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.heroScrollProgress = Math.min(window.scrollY / 700, 1);
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closeLightbox();
    }

    get lightboxImage(): GalleryImage | null {
        return this.lightboxIndex === null ? null : this.galleryImages[this.lightboxIndex];
    }

    goToSlide(index: number): void {
        this.activeSlide = index;
    }

    openRestaurantGuru(event: Event): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if ((event.target as HTMLElement).closest('a')) {
            return;
        }

        window.open(
            'https://restaurantguru.com/Metzgerei-Scharfetter-Saint-Johann-im-Pongau',
            '_blank',
            'noopener'
        );
    }

    openLightbox(index: number): void {
        this.lightboxIndex = index;
    }

    closeLightbox(): void {
        this.lightboxIndex = null;
    }

    stepLightbox(step: number, event: Event): void {
        event.stopPropagation();

        if (this.lightboxIndex === null) {
            return;
        }

        const count: number = this.galleryImages.length;
        this.lightboxIndex = (this.lightboxIndex + step + count) % count;
    }

    /** Menus for today, or the next weekday with a menu when we are on a weekend. */
    private resolveTodayMenus(weekdayMenus: WeekdayMenu[]): Menu[] {
        const target: WeekdayMenu | undefined = this.todayWeekday
            ? weekdayMenus.find(weekdayMenu => weekdayMenu.day === this.todayWeekday)
            : weekdayMenus.find(weekdayMenu => !!weekdayMenu.menu1?.hauptspeise || !!weekdayMenu.menu2?.hauptspeise);

        if (!target) {
            return [];
        }

        this.todayWeekday = target.day;

        return [target.menu1, target.menu2].filter((menu): menu is Menu => !!menu?.hauptspeise);
    }

    private resolveOpeningState(now: Date): OpeningState {
        const day: number = now.getDay();
        const minutes: number = now.getHours() * 60 + now.getMinutes();

        if (day >= 1 && day <= 5) {
            const open: boolean = minutes >= 7 * 60 + 30 && minutes < 18 * 60;

            return {
                open: open,
                label: open ? 'Jetzt geöffnet · bis 18:00 Uhr' : 'Heute 07:30 – 18:00 Uhr'
            };
        }

        if (day === 6) {
            const open: boolean = minutes >= 7 * 60 && minutes < 12 * 60;

            return {
                open: open,
                label: open ? 'Jetzt geöffnet · bis 12:00 Uhr' : 'Samstag 07:00 – 12:00 Uhr'
            };
        }

        return {open: false, label: 'Sonntag geschlossen · Mo ab 07:30 Uhr'};
    }
}
